import React, { useMemo, useState } from 'react';
import { listPoses, poseThumb, resolvePoseRef, POSE_SHORTCUTS } from './poseImages';
import {
  defaultHoldForStyle,
  MAX_HOLD_SECONDS,
  normalizeSequence,
  sequenceDurationMins,
  sequencePoseCount,
  stepPoseLabel,
} from './sequenceModel';

const SHORTCUT_BY_NAME = Object.fromEntries(
  Object.entries(POSE_SHORTCUTS).map(([short, name]) => [name, short]),
);

function emptyStep(style) {
  return {
    poseKey: 'mountain',
    holdSeconds: defaultHoldForStyle(style),
    cue: '',
    breath: '',
    tip: '',
    mod: '',
  };
}

function PoseSearch({ onPick }) {
  const [query, setQuery] = useState('');
  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return listPoses({ query: q, visibility: 'primary' }).slice(0, 8);
  }, [query]);

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
        <span className="u-xs u-muted">Add pose</span>
        <input
          className="cs-input"
          type="search"
          placeholder="Search Pocket Yoga (mountain, downward dog…)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {results.length ? (
        <div className="yoga-seq-search">
          {results.map((pose) => {
            const thumb = poseThumb(pose);
            const short = SHORTCUT_BY_NAME[pose.name] || pose.name;
            return (
              <button
                key={pose.name}
                type="button"
                className="yoga-seq-search__item"
                onClick={() => {
                  onPick(short);
                  setQuery('');
                }}
              >
                {thumb?.url ? <img src={thumb.url} alt="" /> : <span className="yoga-seq-search__ph" />}
                <span>
                  {pose.displayName}
                  {short !== pose.name ? (
                    <span className="u-muted u-small"> · {short}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="u-cluster" style={{ '--gap': 'var(--sp-1)' }}>
        {['mountain', 'downwardDog', 'warrior2', 'tree', 'child', 'corpse'].map((key) => (
          <button
            key={key}
            type="button"
            className="cs-chip"
            onClick={() => onPick(key)}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepRow({ step, index, total, style, onChange, onMove, onRemove, onDuplicateSide }) {
  const img = resolvePoseRef(step.poseKey);
  return (
    <div className="yoga-seq-step">
      <div className="yoga-seq-step__head u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
        <span className="u-muted u-small">{index + 1}</span>
        {img?.url ? (
          <img src={img.url} alt="" className="yoga-seq-step__thumb" />
        ) : (
          <span className="yoga-seq-step__thumb yoga-seq-step__thumb--empty" />
        )}
        <strong style={{ flex: 1 }}>{stepPoseLabel(step)}</strong>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move up">
          ↑
        </button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={index >= total - 1} onClick={() => onMove(1)} aria-label="Move down">
          ↓
        </button>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onRemove} aria-label="Remove step">
          Remove
        </button>
      </div>
      <div className="yoga-seq-step__fields">
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Pose key</span>
          <input
            className="cs-input"
            value={step.poseKey}
            onChange={(e) => onChange({ ...step, poseKey: e.target.value.trim() })}
          />
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Hold (seconds)</span>
          <input
            className="cs-input"
            type="number"
            min={5}
            max={MAX_HOLD_SECONDS}
            value={step.holdSeconds}
            onChange={(e) => onChange({
              ...step,
              holdSeconds: Math.min(
                MAX_HOLD_SECONDS,
                Math.max(5, Number(e.target.value) || defaultHoldForStyle(style)),
              ),
            })}
          />
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Side</span>
          <select
            className="cs-input"
            value={step.side || ''}
            onChange={(e) => {
              const v = e.target.value;
              const next = { ...step };
              if (v) next.side = v;
              else delete next.side;
              onChange(next);
            }}
          >
            <option value="">Both / none</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)', gridColumn: '1 / -1' }}>
          <span className="u-xs u-muted">Cue</span>
          <input
            className="cs-input"
            value={step.cue || ''}
            onChange={(e) => onChange({ ...step, cue: e.target.value })}
          />
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)', gridColumn: '1 / -1' }}>
          <span className="u-xs u-muted">Breath</span>
          <input
            className="cs-input"
            value={step.breath || ''}
            onChange={(e) => onChange({ ...step, breath: e.target.value })}
            placeholder="Inhale 4, exhale 6…"
          />
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Tip</span>
          <input
            className="cs-input"
            value={step.tip || ''}
            onChange={(e) => onChange({ ...step, tip: e.target.value })}
          />
        </label>
        <label className="u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <span className="u-xs u-muted">Mod</span>
          <input
            className="cs-input"
            value={step.mod || ''}
            onChange={(e) => onChange({ ...step, mod: e.target.value })}
          />
        </label>
      </div>
      {!step.side ? (
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={onDuplicateSide}>
          Duplicate left / right
        </button>
      ) : null}
    </div>
  );
}

/**
 * Edit an ordered pose sequence. Controlled via `steps` / `onChange`.
 */
export default function SequenceEditor({ steps, onChange, style = 'hatha' }) {
  const seq = normalizeSequence(steps, { style });
  const mins = sequenceDurationMins(seq);
  const count = sequencePoseCount(seq);

  const setSeq = (next) => onChange(normalizeSequence(next, { style }));

  const addPose = (poseKey) => {
    if (!resolvePoseRef(poseKey)) return;
    setSeq([...seq, { ...emptyStep(style), poseKey }]);
  };

  const updateAt = (i, step) => {
    const next = seq.slice();
    next[i] = step;
    setSeq(next);
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= seq.length) return;
    const next = seq.slice();
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
    setSeq(next);
  };

  const removeAt = (i) => setSeq(seq.filter((_, idx) => idx !== i));

  const duplicateSide = (i) => {
    const base = seq[i];
    const left = { ...base, side: 'left' };
    const right = { ...base, side: 'right' };
    const next = [...seq.slice(0, i), left, right, ...seq.slice(i + 1)];
    setSeq(next);
  };

  return (
    <div className="yoga-seq-editor u-stack" style={{ '--gap': 'var(--sp-4)' }}>
      <div className="u-cluster" style={{ '--gap': 'var(--sp-3)', alignItems: 'baseline' }}>
        <strong>Sequence</strong>
        <span className="u-muted u-small">
          {count} pose{count === 1 ? '' : 's'} · ~{mins} min
        </span>
      </div>

      <PoseSearch onPick={addPose} />

      {!seq.length ? (
        <p className="u-muted u-small" style={{ margin: 0 }}>
          Add at least one pose. New classes require a sequence to save.
        </p>
      ) : null}

      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {seq.map((step, i) => (
          <StepRow
            key={`${step.poseKey}-${i}`}
            step={step}
            index={i}
            total={seq.length}
            style={style}
            onChange={(s) => updateAt(i, s)}
            onMove={(dir) => move(i, dir)}
            onRemove={() => removeAt(i)}
            onDuplicateSide={() => duplicateSide(i)}
          />
        ))}
      </div>
    </div>
  );
}
